/**
 * Dimension Design Tokens
 * Standardized sizes for components and layouts
 */

// Spacing scale (based on 8px grid)
export const spacing = {
  0: 0,
  0.5: 4,   // 0.5 * 8
  1: 8,     // 1 * 8
  1.5: 12,  // 1.5 * 8
  2: 16,    // 2 * 8
  2.5: 20,  // 2.5 * 8
  3: 24,    // 3 * 8
  4: 32,    // 4 * 8
  5: 40,    // 5 * 8
  6: 48,    // 6 * 8
  8: 64,    // 8 * 8
  10: 80,   // 10 * 8
  12: 96,   // 12 * 8
  16: 128,  // 16 * 8
};

// Typography dimensions
export const typography = {
  // Font sizes (rem)
  fontSize: {
    xs: '0.75rem',     // 12px - Tiny labels
    sm: '0.8125rem',   // 13px - Helper text
    base: '0.875rem',  // 14px - Standard text (industry standard default)
    md: '1rem',        // 16px - Emphasis text
    lg: '1.125rem',    // 18px - Headers
    xl: '1.375rem',    // 22px - Page titles
    '2xl': '1.625rem', // 26px - Main headers
    '3xl': '2rem',     // 32px - Hero text
  },
  
  // Font weights
  fontWeight: {
    light: 300,
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  
  // Line heights
  lineHeight: {
    tight: 1.2,
    snug: 1.375,
    normal: 1.5,
    relaxed: 1.75,
    loose: 2,
  },
  
  // Letter spacing
  letterSpacing: {
    tight: '-0.02em',
    normal: '0',
    wide: '0.02em',
    wider: '0.04em',
    widest: '0.08em',
  },
};

// Component dimensions
export const components = {
  // Panel dimensions
  panel: {
    headerHeight: 44,
    headerHeightCompact: 36,
    minWidth: 200,
    defaultWidth: 300,
    maxWidth: 600,
    borderWidth: 1,
  },
  
  // Input field dimensions
  input: {
    height: {
      small: 28,
      medium: 32,
      large: 40,
    },
    padding: {
      x: 14,
      y: 8,
    },
    borderRadius: 4,
    borderWidth: 1,
  },
  
  // Button dimensions
  button: {
    height: {
      small: 24,
      medium: 32,
      large: 40,
    },
    padding: {
      small: { x: 8, y: 4 },
      medium: { x: 16, y: 8 },
      large: { x: 24, y: 12 },
    },
    iconSize: {
      small: 16,
      medium: 20,
      large: 24,
    },
    borderRadius: 4,
  },
  
  // Icon dimensions
  icon: {
    xs: 12,
    small: 16,
    medium: 20,
    large: 24,
    xl: 32,
  },
  
  // Chip dimensions
  chip: {
    height: {
      small: 22,
      medium: 28,
      large: 32,
    },
    padding: {
      x: 8,
      y: 4,
    },
  },
  
  // Card dimensions
  card: {
    borderRadius: 4,
    padding: {
      small: 8,
      medium: 12,
      large: 16,
    },
  },
  
  // Dialog dimensions
  dialog: {
    minWidth: 400,
    maxWidth: '90vw',
    minHeight: 300,
    maxHeight: '80vh',
    borderRadius: 4,
  },
  
  // DataGrid dimensions
  dataGrid: {
    rowHeight: 36,
    headerHeight: 44,
    footerHeight: 52,
    columnHeaderHeight: 36,
  },
  
  // Tree view dimensions
  treeView: {
    itemHeight: 32,
    indentSize: 24,
    iconSize: 18,
  },
};

// Border radius scale
export const borderRadius = {
  none: 0,
  sm: 2,
  default: 4,
  md: 6,
  lg: 8,
  xl: 12,
  full: 9999,
};

// Shadow depths
export const shadows = {
  none: 'none',
  sm: '0 1px 2px rgba(0, 0, 0, 0.05)',
  default: '0 2px 4px rgba(0, 0, 0, 0.1)',
  md: '0 4px 8px rgba(0, 0, 0, 0.12)',
  lg: '0 8px 16px rgba(0, 0, 0, 0.15)',
  xl: '0 16px 32px rgba(0, 0, 0, 0.18)',
  '2xl': '0 24px 48px rgba(0, 0, 0, 0.2)',
  
  // Special shadows
  panel: '0 2px 4px rgba(0, 0, 0, 0.1)',
  dialog: '0 8px 32px rgba(0, 0, 0, 0.2)',
  popover: '0 4px 16px rgba(0, 0, 0, 0.15)',
};

// Z-index scale
export const zIndex = {
  hide: -1,
  base: 0,
  dropdown: 1000,
  sticky: 1100,
  fixed: 1200,
  overlay: 1300,
  modal: 1400,
  popover: 1500,
  tooltip: 1600,
  notification: 1700,
};

// Breakpoints
export const breakpoints = {
  xs: 0,
  sm: 600,
  md: 900,
  lg: 1200,
  xl: 1536,
};

// Animation durations
export const duration = {
  instant: 0,
  fast: 150,
  normal: 250,
  slow: 350,
  slower: 500,
};

// Animation easings
export const easing = {
  linear: 'linear',
  easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
  easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
  easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
  sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
};

export default {
  spacing,
  typography,
  components,
  borderRadius,
  shadows,
  zIndex,
  breakpoints,
  duration,
  easing,
};